import api from "./api";
import { API_ENDPOINTS, USE_MOCK_DATA } from "../constants/config";
import { mockTickets, generateMockTickets, mockSites, mockCustomers, mockEngineers, persistMockTickets } from "../data/mockData";
import { TICKET_STATUSES } from "../constants/statuses";

class TicketService {
  // Get all tickets
  async getAllTickets() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, data: mockTickets });
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.TICKETS);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching tickets:", error);
      return { success: false, error: error.message };
    }
  }

  // Get ticket dashboard stats
  async getDashboardStats() {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const { getDashboardStats: mockStatsFunc } = require('../data/mockData');
          resolve({ success: true, data: mockStatsFunc(mockTickets) });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKETS}stats`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      return { success: false, error: error.message };
    }
  }

  // Create new ticket
  // Create new ticket
  async createTicket(ticketData) {
    // 1. Sanitize the payload before doing anything
    const sanitizedData = { ...ticketData };

    // Remove empty ticket_type (Customer scenario)
    if (sanitizedData.ticket_type === "") {
      delete sanitizedData.ticket_type;
    }

    // Remove custom_problem if not "Other" or if empty
    if (sanitizedData.problem_type !== "Other" || !sanitizedData.custom_problem) {
      delete sanitizedData.custom_problem;
    }

    // Ensure site_id is an integer (Context APIs sometimes return strings)
    if (sanitizedData.site_id) {
      sanitizedData.site_id = parseInt(sanitizedData.site_id, 10);
    }

    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const newTicket = {
            id: mockTickets.length + 1,
            ...sanitizedData,
            status: TICKET_STATUSES.PENDING_ASSIGNMENT,
            created_at: new Date().toISOString(),
            closed_at: null,
            due_date: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
            assignee_id: null,
            assignee_name: null,
            site_name: this.getSiteName(sanitizedData.site_id),
            metrics: {
              thread_count: 0,
              reopen_count: 0,
              attachment_count: 0,
              task_count: 0,
              sla_due_date: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
              sla_plan: "48 Hour Response",
              is_overdue: false,
              is_merged: false,
              is_linked: false,
              is_answered: false,
              engineer_signature_s3_key: null,
              customer_signature_s3_key: null,
            },
          };
          mockTickets.unshift(newTicket);
          persistMockTickets();
          resolve({ success: true, data: newTicket });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.TICKETS, sanitizedData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error creating ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Update ticket
  async updateTicket(ticketId, ticketData) {
    // 1. Sanitize the payload
    const sanitizedData = { ...ticketData };

    if (sanitizedData.ticket_type === "") delete sanitizedData.ticket_type;
    if (sanitizedData.problem_type !== "Other" || !sanitizedData.custom_problem) {
      delete sanitizedData.custom_problem;
    }
    if (sanitizedData.site_id) sanitizedData.site_id = parseInt(sanitizedData.site_id, 10);

    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticketIndex = mockTickets.findIndex((t) => t.id === ticketId);
          if (ticketIndex !== -1) {
            mockTickets[ticketIndex] = {
              ...mockTickets[ticketIndex],
              ...sanitizedData,
              site_name: sanitizedData.site_id ? this.getSiteName(sanitizedData.site_id) : mockTickets[ticketIndex].site_name,
            };
            persistMockTickets();
            resolve({ success: true, data: mockTickets[ticketIndex] });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      const response = await api.put(API_ENDPOINTS.TICKET_DETAILS(ticketId), sanitizedData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error updating ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Delete ticket
  async deleteTicket(ticketId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticketIndex = mockTickets.findIndex((t) => t.id === ticketId);
          if (ticketIndex !== -1) {
            mockTickets.splice(ticketIndex, 1);
            persistMockTickets();
            resolve({ success: true });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      await api.delete(API_ENDPOINTS.TICKET_DETAILS(ticketId));
      return { success: true };
    } catch (error) {
      console.error("Error deleting ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Assign ticket to engineer
  async assignTicket(ticketId, engineerId, assignedFor = null) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket) {
            const engineer = mockEngineers.find((eng) => Number(eng.id) === Number(engineerId));
            ticket.assignee_id = Number(engineerId);
            ticket.assignee_name = engineer?.name || `Engineer ${engineerId}`;
            ticket.status = TICKET_STATUSES.ASSIGNED;
            if (assignedFor) ticket.assigned_for = assignedFor;
            persistMockTickets();
            resolve({ success: true, data: ticket });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      const payload = { assignee_id: engineerId };
      if (assignedFor) payload.assigned_for = assignedFor;
      const response = await api.put(API_ENDPOINTS.TICKET_ASSIGN(ticketId), payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error assigning ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Change ticket status
  async changeStatus(ticketId, status) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket) {
            ticket.status = status;
            if (status === "CLOSED") {
              ticket.closed_at = new Date().toISOString();
            }
            persistMockTickets();
            resolve({ success: true, data: ticket });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      const response = await api.patch(`${API_ENDPOINTS.TICKETS}${ticketId}/status`, { status });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error changing status:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Upload attachment
  async uploadAttachment(ticketId, file) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket && ticket.metrics) {
            ticket.metrics.attachment_count += 1;
            persistMockTickets();
          }
          resolve({
            success: true,
            data: {
              id: Date.now(),
              ticket_id: ticketId,
              file_name: file.name,
              mime_type: file.type,
              s3_object_key: `attachments/${ticketId}/${file.name}`,
              file_size_bytes: file.size,
              created_at: new Date().toISOString(),
            },
          });
        }, 1000);
      });
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(API_ENDPOINTS.TICKET_ATTACHMENTS(ticketId), formData, { headers: { "Content-Type": "multipart/form-data" } });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error uploading attachment:", error);
      return { success: false, error: error.message };
    }
  }

  // Close ticket
  async closeTicket(ticketId, closeData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket) {
            ticket.status = "CLOSED";
            ticket.closed_at = new Date().toISOString();
            if (ticket.metrics) {
              ticket.metrics.engineer_signature_s3_key = closeData.engineer_signature_s3_key;
              ticket.metrics.customer_signature_s3_key = closeData.customer_signature_s3_key;
            }
            persistMockTickets();
            resolve({ success: true, data: ticket });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.TICKET_CLOSE(ticketId), closeData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error closing ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Reopen ticket
  async reopenTicket(ticketId, reasonData) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket) {
            // Revert status to pending assignment and log reason
            ticket.status = TICKET_STATUSES.PENDING_ASSIGNMENT;
            ticket.reopen_reason = reasonData.reason;
            ticket.assignee_id = null; // Clear assignee
            ticket.assignee_name = null;
            persistMockTickets();
            resolve({ success: true, data: ticket });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.TICKET_REOPEN(ticketId), reasonData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error reopening ticket:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Get ticket by ID
  async getTicketById(ticketId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket) {
            resolve({ success: true, data: ticket });
          } else {
            resolve({ success: false, error: "Ticket not found" });
          }
        }, 300);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.TICKET_DETAILS(ticketId));
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching ticket:", error);
      return { success: false, error: error.message };
    }
  }

  // Download service report PDF
  async downloadServiceReport(ticketId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          console.log("Mock: Downloading service report for ticket", ticketId);
          // In real implementation, this would generate a PDF
          alert("Mock: Service report would be downloaded here");
          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const response = await api.get(API_ENDPOINTS.TICKET_REPORT_PDF(ticketId), { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `service-report-${ticketId}-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };
    } catch (error) {
      console.error("Error downloading service report:", error);
      return { success: false, error: error.message };
    }
  }

  // Download ticket attachment
  async downloadAttachment(ticketId, attachment) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const content = `Mock attachment content for ticket #${ticketId}`;
          const blob = new Blob([content], { type: attachment?.mime_type || "application/octet-stream" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", attachment?.file_name || `attachment-${Date.now()}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          resolve({ success: true });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKET_ATTACHMENTS(ticketId)}/${attachment.id}/download`, { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment?.file_name || `attachment-${Date.now()}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Error downloading attachment:", error);
      return { success: false, error: error.message };
    }
  }

  // Preview ticket attachment
  async previewAttachment(ticketId, attachment) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const content = `Mock attachment content for ticket #${ticketId}`;
          const blob = new Blob([content], { type: attachment?.mime_type || "text/plain" });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          resolve({ success: true });
        }, 300);
      });
    }

    try {
      const response = await api.get(`${API_ENDPOINTS.TICKET_ATTACHMENTS(ticketId)}/${attachment.id}/download`, { responseType: "blob" });

      const blob = new Blob([response.data], { type: attachment?.mime_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // We don't revoke here because the new tab needs to read from memory

      return { success: true };
    } catch (error) {
      console.error("Error previewing attachment:", error);
      return { success: false, error: error.message };
    }
  }

  // Export tickets as CSV
  async exportTicketsCSV(filters = {}) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          // Expanded headers to match View Modal details
          const headers = [
            "ID",
            "Title",
            "Description",
            "Status",
            "Priority",
            "Ticket Type",
            "System Type",
            "Problem Type",
            "Custom Problem",
            "Site",
            "Assignee",
            "Source",
            "Created At",
            "Assignment Time",
            "Due Date",
            "Closed At",
          ];

          const rows = mockTickets.map((t) => [
            t.id,
            `"${(t.title || "").replace(/"/g, '""')}"`, // Escape quotes and wrap in quotes to handle commas
            `"${(t.description || "").replace(/"/g, '""')}"`,
            t.status,
            t.priority,
            t.ticket_type || "",
            t.system_type || "",
            t.problem_type || "",
            t.custom_problem || "",
            `"${(t.site_name || "").replace(/"/g, '""')}"`,
            `"${(t.assignee_name || "Unassigned").replace(/"/g, '""')}"`,
            t.source || "Web",
            t.created_at ? new Date(t.created_at).toLocaleString() : "",
            t.time_to_attend ? new Date(t.time_to_attend).toLocaleString() : "",
            t.due_date ? new Date(t.due_date).toLocaleString() : "",
            t.closed_at ? new Date(t.closed_at).toLocaleString() : "",
          ]);

          const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
          const blob = new Blob([csv], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `tickets-export-${Date.now()}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);

          resolve({ success: true });
        }, 500);
      });
    }

    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assignee) params.assignee_id = filters.assignee;
      if (filters.site) params.site_id = filters.site;
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.activeOnly) params.active_only = true;
      if (filters.closedToday) params.closed_today = true;
      if (filters.overdue) params.overdue = true;

      const response = await api.get(API_ENDPOINTS.TICKETS_EXPORT_CSV, { responseType: "blob", params });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `tickets-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Error exporting tickets:", error);
      return { success: false, error: error.message };
    }
  }

  // Helper method to get site name by ID (for mock data)
  getSiteName(siteId) {
    const sites = { 1: "Site A - Tech Park", 2: "Site B - Main Office", 3: "Site C - Warehouse", 4: "Site D - Retail Store" };
    return sites[siteId] || "Unknown Site";
  }

  // Bulk assign tickets
  async bulkAssignTickets(ticketIds, engineerId) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const updatedTickets = [];
          ticketIds.forEach((ticketId) => {
            const ticket = mockTickets.find((t) => t.id === ticketId);
            if (ticket) {
              const engineer = mockEngineers.find((eng) => Number(eng.id) === Number(engineerId));
              ticket.assignee_id = Number(engineerId);
              ticket.assignee_name = engineer?.name || `Engineer ${engineerId}`;
              ticket.status = TICKET_STATUSES.ASSIGNED;
              updatedTickets.push(ticket);
            }
          });
          persistMockTickets();
          resolve({ success: true, data: updatedTickets });
        }, 800);
      });
    }

    try {
      const response = await api.post(`${API_ENDPOINTS.TICKETS}bulk-assign`, { ticket_ids: ticketIds, assignee_id: engineerId });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error bulk assigning tickets:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Bulk update status
  async bulkUpdateStatus(ticketIds, status) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const updatedTickets = [];
          ticketIds.forEach((ticketId) => {
            const ticket = mockTickets.find((t) => t.id === ticketId);
            if (ticket) {
              ticket.status = status;
              if (status === "CLOSED") {
                ticket.closed_at = new Date().toISOString();
              }
              updatedTickets.push(ticket);
            }
          });
          persistMockTickets();
          resolve({ success: true, data: updatedTickets });
        }, 800);
      });
    }

    try {
      const response = await api.post(`${API_ENDPOINTS.TICKETS}bulk-status`, { ticket_ids: ticketIds, status });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error bulk updating status:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }

  // Add comment to ticket
  async addComment(ticketId, comment) {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const ticket = mockTickets.find((t) => t.id === ticketId);
          if (ticket && ticket.metrics) {
            ticket.metrics.thread_count += 1;
            persistMockTickets();
          }
          resolve({ success: true, data: { id: Date.now(), ticket_id: ticketId, comment: comment, created_at: new Date().toISOString(), created_by: "Current User" } });
        }, 500);
      });
    }

    try {
      const response = await api.post(API_ENDPOINTS.TICKET_COMMENTS(ticketId), { comment });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error adding comment:", error);
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }
  // Create ticket for customer (simplified)
  // Create ticket for customer (simplified)
  // async createCustomerTicket(ticketData) {
  //   if (USE_MOCK_DATA) {
  //     return new Promise((resolve) => {
  //       setTimeout(() => {
  //         // Find the site to get site_name and customer_id
  //         const site = mockSites.find(s => s.id === parseInt(ticketData.site_id));

  //         const newTicket = {
  //           id: mockTickets.length + 1,
  //           ...ticketData,
  //           site_name: site ? site.site_name : 'Unknown Site',
  //           customer_id: site ? site.customer_id : null,
  //           status: TICKET_STATUSES.OPEN,
  //           created_at: new Date().toISOString(),
  //           closed_at: null,
  //           due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  //           assignee_id: null,
  //           assignee_name: null,
  //           source: 'Web',
  //           department_id: ticketData.department_id || 2,
  //           metrics: {
  //             thread_count: 0,
  //             reopen_count: 0,
  //             attachment_count: 0,
  //             task_count: 0,
  //             sla_due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  //             sla_plan: '24 Hour Response',
  //             is_overdue: false,
  //             is_merged: false,
  //             is_linked: false,
  //             is_answered: false,
  //             engineer_signature_s3_key: null,
  //             customer_signature_s3_key: null
  //           }
  //         };

  //         mockTickets.unshift(newTicket);

  //         // Update site counts
  //         if (site) {
  //           site.total_tickets += 1;
  //           site.active_tickets += 1;
  //         }

  //         // Update customer counts
  //         const customer = mockCustomers.find(c => c.id === site?.customer_id);
  //         if (customer) {
  //           customer.total_tickets += 1;
  //           customer.open_tickets += 1;
  //         }

  //         resolve({ success: true, data: newTicket });
  //       }, 500);
  //     });
  //   }

  //   try {
  //     const response = await api.post(API_ENDPOINTS.TICKETS, ticketData);
  //     return { success: true, data: response.data };
  //   } catch (error) {
  //     console.error('Error creating customer ticket:', error);
  //     return { success: false, error: error.response?.data?.detail || error.message };
  //   }
  // }
}

export default new TicketService();
